import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../../context/MyContext";
import { backIncstance } from "../../../utils/commonFunctions";
import {
  useInfiniteSocratesResultsQuery,
  SocratesResultItem,
} from "./useInfiniteSocratesResultsQuery";

const URLS = {
  CURIOSITY_RATE: process.env.REACT_APP_MYPAGE_CURIOSITY_RATE,
  ACTIVITY_TIME: process.env.REACT_APP_MYPAGE_AVTIVE_TIME,
  TOP_KEYWORD: process.env.REACT_APP_MYPAGE_TOP_KEYWORD,
  RECOMMENT_KEYWORD: process.env.REACT_APP_MYPAGE_RECOMMENT_KEYWORD,
};

export interface CuriosityData {
  tutorCount: number;
  avgQuestionsPerTutor: number;
  socratesCount: number;
}

export interface ActivityTimeData {
  dayList: { day: string; dayName: string; value: number }[];
  topDate: string | null;
}

export interface RecommendKeywordData {
  keywords: string[];
  percentage: number | null;
}

export type { SocratesResultItem };

export const useMyActivityView = () => {
  const navigate = useNavigate();
  const { user, setAlertSetting, clearState, isLogin, setIsLogin } =
    useContext(MyContext);

  const [loading, setLoading] = useState(true);

  // 호기심 지수
  const [curiosity, setCuriosity] = useState<CuriosityData>({
    tutorCount: 0,
    avgQuestionsPerTutor: 0,
    socratesCount: 0,
  });

  // 활동 시간대
  const [activityTime, setActivityTime] = useState<ActivityTimeData>({
    dayList: [],
    topDate: null,
  });

  // 관심 키워드 TOP5
  const [topKeywords, setTopKeywords] = useState<string[]>([]);

  // 추천 키워드
  const [recommendKeywords, setRecommendKeywords] =
    useState<RecommendKeywordData>({
      keywords: [],
      percentage: null,
    });

  // 소크라테스 결과 - 무한 스크롤 Query 사용
  const {
    data: socratesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isSocratesLoading,
  } = useInfiniteSocratesResultsQuery({ limit: 8 });

  // 결과 평탄화
  const socratesResults =
    socratesData?.pages.flatMap((page) => page.results) || [];
  const socratesTotal = socratesData?.pages[0]?.totalCount || 0;

  const touchAPI = async (method: string, url: string, data?: string) => {
    try {
      const response = await backIncstance(method, url, data);

      if (response.status === 401) {
        setAlertSetting((prev: any) => ({
          ...prev,
          alertTxt: "로그인이 필요합니다.",
          onClose: () => {
            clearState();
            navigate("/login", {
              state: {
                from_chat: null,
                his_cnt: 0,
                is_writed: false,
                is_completed: false,
              },
            });
          },
        }));
        return null;
      }

      const resData = await response.json();
      return resData;
    } catch (err) {
      console.error("API Error:", err);
      setAlertSetting((prev: any) => ({
        ...prev,
        alertTxt: `서버 오류입니다.\n관리자에게 문의 바랍니다.`,
        btnTxt: "닫기",
      }));
      return null;
    }
  };

  // 호기심 지수 조회
  const fetchCuriosityRate = async () => {
    const data = await touchAPI("GET", URLS.CURIOSITY_RATE as string);
    if (data) {
      setCuriosity({
        tutorCount: data.tt_tutor_cnt || 0,
        avgQuestionsPerTutor: data.add_tutor_cnt || 0,
        socratesCount: data.tt_socra_cnt || 0,
      });
    }
  };

  // 활동 시간대 조회
  const fetchActivityTime = async () => {
    const data = await touchAPI("GET", URLS.ACTIVITY_TIME as string);
    if (data) {
      const dayNames: Record<string, string> = {
        mon: "월",
        tue: "화",
        wed: "수",
        thu: "목",
        fri: "금",
        sat: "토",
        sun: "일",
      };

      const dayList = Object.keys(dayNames).map((key) => ({
        day: key,
        dayName: dayNames[key],
        value: data[key] || 0,
      }));

      setActivityTime({
        dayList,
        topDate: data.top_date || null,
      });
    }
  };

  // 관심 키워드 TOP5 조회
  const fetchTopKeywords = async () => {
    const data = await touchAPI("GET", URLS.TOP_KEYWORD as string);
    if (data?.keyword_list) {
      const keywords = data.keyword_list.map(
        (item: { keyword: string }) => item.keyword
      );
      setTopKeywords(keywords.slice(0, 5));
    }
  };

  // 추천 키워드 조회
  const fetchRecommendKeywords = async () => {
    const data = await touchAPI("GET", URLS.RECOMMENT_KEYWORD as string);
    if (data) {
      const keywords =
        data.keyword_list?.map((item: { keyword: string }) => item.keyword) ||
        [];
      setRecommendKeywords({
        keywords,
        percentage: data.sim_user_percent ?? null,
      });
    }
  };

  // 결과보기 클릭 핸들러
  const handleViewResult = (id: number) => {
    navigate(`/socrates/${id}/result`);
  };

  // 뒤로가기
  const handleGoBack = () => {
    navigate(-1);
  };

  // 데이터 로드 (소크라테스 결과는 Query가 자동 처리)
  useEffect(() => {
    if (!user?.id) {
      setIsLogin(true);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCuriosityRate(),
        fetchActivityTime(),
        fetchTopKeywords(),
        fetchRecommendKeywords(),
      ]);
      setLoading(false);
    };

    loadData();
  }, [user]);

  return {
    loading: loading || isSocratesLoading,
    curiosity,
    activityTime,
    topKeywords,
    recommendKeywords,
    socratesResults,
    socratesTotal,
    handleViewResult,
    handleGoBack,
    isLogin,
    // 무한 스크롤 관련
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export default useMyActivityView;
